import { z } from 'zod';
import { InstantSchema } from './time';

/**
 * Operational readiness (OPS-006, OPS-007).
 *
 * What an operator needs to know that the running system will not tell a user: which build is
 * running, whether the database schema matches it, whether the worker is alive and how many jobs
 * died, whether each scheduled sweep ran and how it ended, and which providers are healthy. All of it
 * is **redacted by construction**: configuration is reported as present or absent, never as a value.
 */

export const BuildInfoSchema = z.strictObject({
  version: z.string(),
  /** Short commit hash at build time, `+dirty` when built from uncommitted changes. */
  commit: z.string(),
  builtAt: z.string(),
});
export type BuildInfo = z.infer<typeof BuildInfoSchema>;

/** The Redis key the worker refreshes while it is alive. */
export const WORKER_HEARTBEAT_KEY = 'alola:worker:heartbeat';
/** How long a heartbeat stays valid; the worker refreshes it three times as often. */
export const WORKER_HEARTBEAT_TTL_SECONDS = 90;

export const WorkerHeartbeatSchema = z.strictObject({
  at: InstantSchema,
  build: BuildInfoSchema,
  /** Jobs that used every attempt and wait for an operator. */
  deadLetters: z.number().int().nonnegative(),
});
export type WorkerHeartbeat = z.infer<typeof WorkerHeartbeatSchema>;

export const MAINTENANCE_SWEEPS = [
  'notifications.dispatch',
  'tasks.sweep',
  'approvals.escalate',
  'installments.refresh',
  'finance.sweep',
  'collections.receivables',
  'integrations.sweep',
  'inventory.sweep',
  'sales.sweep',
  /** Decided treasury, payment, reconciliation and endorsement approvals; instrument due notices. */
  'treasury.sweep',
  /** Import retention: uploaded files and stored rows cleared after the retention period (Release 1). */
  'imports.retention',
  'budgets.sweep',
  'construction.sweep',
  'assets.sweep',
  'hr.sweep',
  'payroll.sweep',
  'expenses.sweep',
  'handover.sweep',
  'stores.sweep',
  'crm.autoRecycle',
] as const;
export const MaintenanceSweepSchema = z.enum(MAINTENANCE_SWEEPS);
export type MaintenanceSweep = z.infer<typeof MaintenanceSweepSchema>;

export const MaintenanceRunSchema = z.strictObject({
  sweep: MaintenanceSweepSchema,
  lastStartedAt: InstantSchema.optional(),
  lastFinishedAt: InstantSchema.optional(),
  lastOutcome: z.enum(['succeeded', 'failed']).optional(),
  /** A stable code, never a message or a stack. */
  lastErrorCode: z.string().optional(),
  /** Counts the sweep reported, e.g. `{ escalated: 2 }`. */
  lastResult: z.record(z.string(), z.number()).optional(),
  nextRunAt: InstantSchema.optional(),
  /** True while one instance holds the lease. */
  running: z.boolean(),
});
export type MaintenanceRun = z.infer<typeof MaintenanceRunSchema>;

export const DiagnosticsSchema = z.strictObject({
  build: BuildInfoSchema,
  appEnv: z.string(),
  nodeVersion: z.string(),
  uptimeSeconds: z.number().int().nonnegative(),
  migrations: z.strictObject({
    databaseVersion: z.string(),
    codeVersion: z.string(),
    pending: z.array(z.string()),
    changed: z.array(z.string()),
    unknown: z.array(z.string()),
  }),
  maintenance: z.strictObject({
    enabled: z.boolean(),
    runs: z.array(MaintenanceRunSchema),
  }),
  worker: z.strictObject({
    status: z.enum(['alive', 'absent']),
    heartbeat: WorkerHeartbeatSchema.optional(),
  }),
  integrations: z.record(z.string(), z.number().int().nonnegative()),
  /** Each configuration variable as set or not set — never its value. */
  configuration: z.record(z.string(), z.boolean()),
});
export type Diagnostics = z.infer<typeof DiagnosticsSchema>;

/* ------------------------------------------------------------- configuration readiness */

/**
 * Release-1 configuration readiness (ADR-0038): what a deployment still needs before it can issue
 * documents, post to the ledger or launch, shown to administrators on one screen.
 *
 * Every check reports a status, what a missing item blocks, and **safe metadata only** — counts,
 * provider kinds, a key id, a timestamp. Never a value that is secret or personal: not a key, not a
 * path, not a registration number, not an account number.
 */
export const READINESS_AREAS = [
  'company',
  'documents',
  'treasury',
  'accounting',
  'approvals',
  /** Dashboard-configured lists that items, counts and expenses use (reference data). */
  'masterData',
  'platform',
  'integrations',
] as const;
export type ReadinessArea = (typeof READINESS_AREAS)[number];

export const READINESS_CHECKS = [
  'company.identity',
  'company.arabicIdentity',
  'company.englishIdentity',
  'company.legalName',
  'company.commercialRegistration',
  'company.taxRegistration',
  'company.address',
  'company.contact',
  'company.signatories',
  'company.logo',
  'company.logoAr',
  'company.logoEn',
  'company.compactLogo',
  'company.favicon',
  'company.documentBranding',
  'company.brandAccessibility',
  'company.stamp',
  'documents.numbering',
  'documents.taxCodes',
  'treasury.bankAccounts',
  'treasury.cashboxes',
  'accounting.fiscalYear',
  'accounting.chartOfAccounts',
  'accounting.postingRules',
  'accounting.postingCoverage',
  'accounting.openingBalances',
  'accounting.onboardingAdoption',
  'accounting.revenueRecognition',
  'accounting.statementLines',
  'accounting.stockValuation',
  'approvals.policies',
  'approvals.authorityLimits',
  'approvals.supplierAwards',
  'approvals.cashAdvances',
  'approvals.directPurchases',
  'masterData.commissionRules',
  'masterData.leadDistribution',
  'masterData.itemCategories',
  'masterData.unitsOfMeasure',
  'masterData.expenseCategories',
  'platform.encryption',
  'platform.fileStorage',
  'platform.backups',
  'platform.publicUrl',
  'integrations.providers',
  'integrations.messaging',
] as const;
export type ReadinessCheckKey = (typeof READINESS_CHECKS)[number];

/** `optional` = not done, and nothing depends on it. */
export const READINESS_STATUSES = ['complete', 'incomplete', 'optional'] as const;
export type ReadinessStatus = (typeof READINESS_STATUSES)[number];

export const READINESS_NEEDS = [
  'optional',
  'documentIssuance',
  'financialPosting',
  /** Recognized revenue and project profit in the profitability report (BD-71). */
  'profitReporting',
  /** The income statement, financial position and cash flow subtotals (FIN-REPORT-009…011). */
  'financialStatements',
  /** Purchase orders generated from approved supplier awards (owner decision, 2026-10-17). */
  'purchaseOrders',
  /** Paying cash advances (owner decision, 2026-10-17). */
  'cashAdvances',
  'productionLaunch',
] as const;

/**
 * What an open item is waiting for, so nobody mistakes one for another:
 * - `clientConfiguration` — a **value** the client enters in the dashboard (the feature is built);
 * - `infrastructure` — the server: keys, storage, backups, the public address;
 * - `externalProvider` — an account or contract with an outside provider, then its credentials;
 * - `notBuilt` — **code that does not exist yet**; no configuration closes it.
 */
export const READINESS_PENDING_KINDS = [
  'clientConfiguration',
  'infrastructure',
  'externalProvider',
  'notBuilt',
] as const;

export const ReadinessCheckSchema = z.strictObject({
  key: z.enum(READINESS_CHECKS),
  area: z.enum(READINESS_AREAS),
  status: z.enum(READINESS_STATUSES),
  pending: z.enum(READINESS_PENDING_KINDS),
  needs: z.array(z.enum(READINESS_NEEDS)),
  /** Changes to this value are versioned: each one kept with who, when and why, earlier ones readable. */
  versioned: z.boolean(),
  /** Safe metadata: counts, provider kinds, a key id, a date — never a secret or personal value. */
  detail: z.record(z.string(), z.union([z.string().max(80), z.number(), z.boolean()])).default({}),
});
export type ReadinessCheck = z.infer<typeof ReadinessCheckSchema>;

export const ReadinessSchema = z.strictObject({
  generatedAt: InstantSchema,
  appEnv: z.string(),
  checks: z.array(ReadinessCheckSchema),
  summary: z.strictObject({
    complete: z.number().int().nonnegative(),
    incomplete: z.number().int().nonnegative(),
    optional: z.number().int().nonnegative(),
  }),
});
export type Readiness = z.infer<typeof ReadinessSchema>;
